const message = document.querySelector('#account-message');
const formPanel = document.querySelector('#account-form-panel');
const sessionPanel = document.querySelector('#account-session');
const signout = document.querySelector('#account-signout');
const controls = document.querySelectorAll('[data-account-mode]');
const copyRelayAddress = document.querySelector('#copy-relay-address');
const relayCopyMessage = document.querySelector('#relay-copy-message');
const relay = copyRelayAddress.dataset.relayUrl;
const client = 'runthingsnow-web';
const callback = `${location.origin}/oauth/callback`;
const transactionKey = 'backgrounder-signin';
const transactionLifetime = 10 * 60 * 1000;
const authParams = ['code', 'state', 'iss', 'error', 'error_description', 'error_uri'];
const callbackParams = new URLSearchParams(location.search);
// Keep existing OAuth returns usable without advertising website sign-in.
document.querySelector('#website-account').hidden = location.pathname !== '/oauth/callback' &&
  !authParams.some(key => callbackParams.has(key));
let busy = false;
let session = null;
let renewalTimer;

function showMessage(text, error = false) {
  message.textContent = text;
  message.classList.toggle('text-danger', error);
}

function setBusy(value) {
  busy = value;
  for (const control of [...controls, signout]) {
    if (control instanceof HTMLButtonElement) control.disabled = value;
    control.setAttribute('aria-disabled', String(value));
  }
  document.querySelector('.account-card').setAttribute('aria-busy', String(value));
}

function clearSession() {
  clearTimeout(renewalTimer);
  session = null;
  document.querySelector('#session-email').textContent = '';
  sessionPanel.hidden = true;
  formPanel.hidden = false;
}

const base64url = bytes => btoa(String.fromCharCode(...bytes))
  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

async function startSignIn(register) {
  if (busy) return;
  if (session) {
    document.querySelector('#website-account').scrollIntoView();
    return;
  }
  setBusy(true);
  showMessage('Opening secure sign-in...');
  try {
    const state = base64url(crypto.getRandomValues(new Uint8Array(32)));
    const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    sessionStorage.setItem(transactionKey, JSON.stringify({
      state, verifier, relay, client, callback, createdAt: Date.now(),
    }));
    const query = new URLSearchParams({
      response_type: 'code', client_id: client, redirect_uri: callback, state,
      code_challenge_method: 'S256', code_challenge: base64url(new Uint8Array(digest)),
    });
    if (register) query.set('screen', 'register');
    location.assign(`${relay}/oauth/authorize?${query}`);
  } catch {
    showMessage('Could not start secure sign-in. Enable browser session storage and use a browser with secure cryptography support, then try again.', true);
    setBusy(false);
    document.querySelector('#website-account').scrollIntoView();
  }
}

async function request(endpoint, fields) {
  let response;
  try {
    response = await fetch(`${relay}/oauth/${endpoint}`, {
      method: 'POST',
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      redirect: 'error',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ client_id: client, ...fields }),
      signal: AbortSignal.timeout(20000),
    });
  } catch {
    throw new Error('Could not reach the sign-in service. Check your connection and start a new sign-in; this request will not be retried.');
  }
  if (!response.ok) {
    throw new Error(response.status >= 500
      ? 'The sign-in service is unavailable. Please start a new sign-in later.'
      : 'The sign-in service refused the request. Please start a new sign-in.');
  }
  if (endpoint === 'revoke') return;
  try {
    return await response.json();
  } catch {
    throw new Error('The sign-in service returned an unreadable response. Please start a new sign-in.');
  }
}

function acceptTokens(data, expectedAccount) {
  if (!data || typeof data.access_token !== 'string' || !data.access_token ||
      typeof data.refresh_token !== 'string' || !data.refresh_token ||
      typeof data.token_type !== 'string' || data.token_type.toLowerCase() !== 'bearer' ||
      !Number.isSafeInteger(data.expires_in) || data.expires_in <= 0 || data.expires_in > 2147483 ||
      typeof data.email !== 'string' || !data.email ||
      typeof data.accountId !== 'string' || !data.accountId ||
      (expectedAccount && expectedAccount !== data.accountId)) {
    throw new Error('The sign-in service returned an invalid session. Please start a new sign-in.');
  }
  // Tokens never leave this tab's memory. The response identifies the account;
  // it does not authorize access to any Mac or protected website API.
  session = {
    accessToken: data.access_token, refreshToken: data.refresh_token,
    accountId: data.accountId, email: data.email,
  };
  document.querySelector('#session-email').textContent = session.email;
  formPanel.hidden = true;
  sessionPanel.hidden = false;
  const lifetime = data.expires_in * 1000;
  clearTimeout(renewalTimer);
  renewalTimer = setTimeout(renewSession, lifetime - Math.min(60000, lifetime / 10));
}

async function renewSession() {
  if (!session) return;
  setBusy(true);
  const { refreshToken, accountId } = session;
  // Retire before sending: replaying a rotated token revokes its whole family.
  session.refreshToken = null;
  try {
    const data = await request('token', { grant_type: 'refresh_token', refresh_token: refreshToken });
    acceptTokens(data, accountId);
  } catch (error) {
    clearSession();
    showMessage(`Your browser session has ended. ${error.message}`, true);
  } finally {
    setBusy(false);
  }
}

async function finishSignIn() {
  const params = new URLSearchParams(location.search);
  if (!authParams.some(key => params.has(key))) return;
  setBusy(true);
  // Strip credentials before rendering results or making any network request.
  const clean = new URL(location.href);
  authParams.forEach(key => clean.searchParams.delete(key));
  history.replaceState(null, '', `${clean.pathname}${clean.search}#website-account`);
  try {
    const stored = sessionStorage.getItem(transactionKey);
    sessionStorage.removeItem(transactionKey);
    let saved;
    try {
      saved = JSON.parse(stored);
    } catch {
      throw new Error('The saved sign-in request is unreadable. Please start a new sign-in.');
    }
    if (!saved || saved.relay !== relay || saved.client !== client || saved.callback !== callback ||
        location.pathname !== '/oauth/callback' ||
        !Number.isSafeInteger(saved.createdAt) || Date.now() < saved.createdAt ||
        Date.now() - saved.createdAt > transactionLifetime ||
        !/^[A-Za-z0-9_-]{43}$/.test(saved.state) || !/^[A-Za-z0-9_-]{43}$/.test(saved.verifier) ||
        params.get('state') !== saved.state || params.get('iss') !== relay ||
        authParams.some(key => params.getAll(key).length > 1) ||
        params.has('code') === params.has('error')) {
      throw new Error('This sign-in response is invalid or expired. Please start a new sign-in.');
    }
    if (params.has('error')) {
      const errors = {
        access_denied: 'Sign-in was cancelled. You can try again when ready.',
        account_deleted: 'Your account was deleted.',
        account_updated: 'Your account settings were updated. Log in again to continue.',
      };
      showMessage(Object.hasOwn(errors, params.get('error'))
        ? errors[params.get('error')] : 'Sign-in was not completed. Please try again.');
      return;
    }
    const code = params.get('code');
    if (!code) throw new Error('The sign-in code is missing. Please start a new sign-in.');
    showMessage('Completing sign-in...');
    const data = await request('token', {
      grant_type: 'authorization_code', redirect_uri: callback, code, code_verifier: saved.verifier,
    });
    acceptTokens(data);
    showMessage('You are logged in.');
  } catch (error) {
    clearSession();
    showMessage(error instanceof DOMException
      ? 'Could not read the saved sign-in request. Enable browser session storage and try again.'
      : error.message, true);
  } finally {
    setBusy(false);
    document.querySelector('#website-account').scrollIntoView();
  }
}

controls.forEach(control => control.addEventListener('click', event => {
  event.preventDefault();
  startSignIn(control.dataset.accountMode === 'register');
}));

signout.addEventListener('click', async () => {
  if (busy || !session) return;
  const refreshToken = session.refreshToken;
  clearSession();
  setBusy(true);
  showMessage('Logging out...');
  try {
    await request('revoke', { token: refreshToken, token_type_hint: 'refresh_token' });
    showMessage('Logged out. Your Mac and other devices remain signed in.');
  } catch (error) {
    showMessage(`Logged out of this page, but the service session could not be revoked. It will expire automatically. ${error.message}`, true);
  } finally {
    setBusy(false);
  }
});

copyRelayAddress.disabled = false;
copyRelayAddress.addEventListener('click', async () => {
  copyRelayAddress.disabled = true;
  relayCopyMessage.textContent = '';
  relayCopyMessage.classList.remove('text-danger');
  try {
    await navigator.clipboard.writeText(relay);
    relayCopyMessage.textContent = 'Relay address copied. Paste it into Settings > Relay Account in Backgrounder.';
  } catch {
    relayCopyMessage.textContent = 'Could not copy the relay address. Check your browser clipboard permissions and try again.';
    relayCopyMessage.classList.add('text-danger');
  } finally {
    copyRelayAddress.disabled = false;
  }
});

window.addEventListener('pageshow', event => {
  if (event.persisted) {
    clearSession();
    setBusy(false);
    showMessage('This page was restored. Log in again to continue.');
  }
});

setBusy(false);
finishSignIn();
